import { ReactNode } from "react";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { LucideIcon } from "lucide-react";

interface Props {
  icon: LucideIcon;
  title: string;
  description?: string;
  enabled?: boolean;
  onToggle?: (v: boolean) => void;
  disabled?: boolean;
  children: ReactNode;
  actions?: ReactNode;
}

export function Section({
  icon: Icon,
  title,
  description,
  enabled,
  onToggle,
  disabled,
  children,
  actions,
}: Props) {
  return (
    <Card className="overflow-hidden border-border/50 bg-card/60 shadow-sm">
      {/* Header — clean, no heavy gradient */}
      <div className="px-5 sm:px-7 py-5 border-b border-border/40 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3.5 min-w-0 flex-1">
          <div className="h-10 w-10 rounded-lg bg-primary/10 ring-1 ring-primary/15 flex items-center justify-center shrink-0">
            <Icon className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold tracking-tight text-foreground leading-tight">{title}</h2>
            {description && (
              <p className="text-[13px] text-muted-foreground mt-0.5 leading-relaxed max-w-2xl">
                {description}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0 justify-between sm:justify-end">
          {actions}
          {onToggle !== undefined && (
            <div className="flex items-center gap-2.5">
              <span className="text-[11px] font-medium text-muted-foreground sm:hidden">
                {enabled ? "Enabled" : "Disabled"}
              </span>
              <Switch
                checked={!!enabled}
                onCheckedChange={onToggle}
                disabled={disabled}
                className="data-[state=checked]:bg-primary"
              />
            </div>
          )}
        </div>
      </div>
      <div className="p-5 sm:p-7 space-y-6">
        {children}
      </div>
    </Card>
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <label className="block text-[13px] font-medium text-foreground/85">
        {label}
      </label>
      <div className="relative group">
        {children}
      </div>
      {hint && (
        <p className="text-xs text-muted-foreground/70 leading-relaxed">
          {hint}
        </p>
      )}
    </div>
  );
}
